/*
 * Design theme filter.
 *
 * Client-side only: every card is already in the DOM, so filtering is a
 * `hidden` toggle rather than a fetch. Nothing here knows a collection handle,
 * a product id or a theme name — all of that arrives from Liquid as data
 * attributes.
 *
 * Init is idempotent and keyed on a WeakMap, so the theme editor can re-render
 * the section as often as it likes.
 */
(function () {
  'use strict';

  var QUERY_KEY = 'theme';
  var instances = new WeakMap();

  function cardThemes(card) {
    var raw = card.getAttribute('data-themes') || '';
    return raw
      .split(',')
      .map(function (value) {
        return value.trim();
      })
      .filter(Boolean);
  }

  function create(root) {
    var chips = Array.prototype.slice.call(root.querySelectorAll('[data-dcs-chip]'));
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-dcs-card]'));
    var empty = root.querySelector('[data-dcs-empty]');
    var reset = root.querySelector('[data-dcs-reset]');

    if (!cards.length) return null;

    var known = chips.map(function (chip) {
      return chip.getAttribute('data-dcs-chip');
    });

    var active = '';
    var listeners = [];

    function on(target, type, handler) {
      target.addEventListener(type, handler);
      listeners.push([target, type, handler]);
    }

    function writeUrl(theme) {
      if (!window.history || !window.history.replaceState) return;

      /*
       * Rebuilding from the live URL keeps any other query parameter intact,
       * and replaceState never moves the scroll position.
       */
      var url = new URL(window.location.href);
      if (theme) {
        url.searchParams.set(QUERY_KEY, theme);
      } else {
        url.searchParams.delete(QUERY_KEY);
      }
      window.history.replaceState({}, '', url.toString());
    }

    function apply(theme, updateUrl) {
      active = known.indexOf(theme) === -1 ? '' : theme;

      chips.forEach(function (chip) {
        chip.setAttribute('aria-pressed', chip.getAttribute('data-dcs-chip') === active ? 'true' : 'false');
      });

      var shown = 0;

      cards.forEach(function (card) {
        var match = active === '' || cardThemes(card).indexOf(active) !== -1;
        var wasHidden = card.hasAttribute('hidden');

        if (match) {
          shown += 1;
          if (wasHidden) {
            card.removeAttribute('hidden');
            // Fade the card in from its hidden state rather than popping it.
            card.classList.add('dcs-design-themes__item--entering');
            requestAnimationFrame(function () {
              requestAnimationFrame(function () {
                card.classList.remove('dcs-design-themes__item--entering');
              });
            });
          }
        } else {
          card.setAttribute('hidden', '');
          card.classList.remove('dcs-design-themes__item--entering');
        }
      });

      if (empty) empty.toggleAttribute('hidden', shown > 0);
      if (updateUrl) writeUrl(active);
    }

    chips.forEach(function (chip) {
      on(chip, 'click', function () {
        var value = chip.getAttribute('data-dcs-chip');
        // Pressing the active chip again clears the filter.
        apply(value === active ? '' : value, true);
      });
    });

    if (reset) {
      on(reset, 'click', function () {
        apply('', true);
        if (chips.length) chips[0].focus();
      });
    }

    /*
     * A design is two products, one per device brand, and the card was built
     * from the iPhone one. Point it at the device the visitor has actually
     * chosen — via ?device= on the URL, or live from the case builder.
     */
    var device = '';

    function applyDevice(value) {
      var wanted = value === 'samsung' ? 'samsung' : 'iphone';
      device = wanted;

      Array.prototype.forEach.call(root.querySelectorAll('[data-dcs-card-link]'), function (link) {
        var href = link.getAttribute('data-url-' + wanted) || link.getAttribute('data-url-iphone');
        if (href) link.setAttribute('href', href);
      });
    }

    /*
     * A Liquid loop over a collection stops at 50 products, so the section
     * paginates and the rest is fetched here — from the section itself, via
     * the Section Rendering API, so Liquid stays the only thing that builds a
     * card or formats a price.
     */
    function loadRemainingPages() {
      var sectionId = root.getAttribute('data-section-id');
      var totalPages = parseInt(root.getAttribute('data-total-pages') || '1', 10);
      var limit = parseInt(root.getAttribute('data-design-limit') || '0', 10);
      var grid = root.querySelector('[data-dcs-grid]');
      var chipList = root.querySelector('[data-dcs-chips]');
      if (!sectionId || !grid || totalPages < 2) return;

      var seen = {};
      cards.forEach(function (card) {
        seen[card.getAttribute('data-design') || ''] = true;
      });

      var chain = Promise.resolve();

      for (var page = 2; page <= totalPages; page += 1) {
        (function (pageNumber) {
          chain = chain.then(function () {
            if (limit && cards.length >= limit) return null;

            var url = new URL(window.location.href);
            url.searchParams.set('section_id', sectionId);
            url.searchParams.set('page', String(pageNumber));

            return fetch(url.toString())
              .then(function (response) {
                return response.ok ? response.text() : null;
              })
              .then(function (html) {
                if (!html) return;
                var parsed = new DOMParser().parseFromString(html, 'text/html');

                Array.prototype.forEach.call(parsed.querySelectorAll('[data-dcs-card]'), function (card) {
                  var key = card.getAttribute('data-design') || '';
                  // A pair split across a page boundary would otherwise appear twice.
                  if (seen[key]) return;
                  if (limit && cards.length >= limit) return;
                  seen[key] = true;
                  grid.appendChild(card);
                  cards.push(card);
                });

                // Later pages can introduce themes the first page never saw.
                if (chipList) {
                  Array.prototype.forEach.call(parsed.querySelectorAll('[data-dcs-chip]'), function (chip) {
                    var value = chip.getAttribute('data-dcs-chip');
                    if (!value || known.indexOf(value) !== -1) return;
                    known.push(value);
                    var item = chip.closest('.dcs-design-themes__chip-item') || chip;
                    chipList.appendChild(item);
                    chips.push(chip);
                    on(chip, 'click', function () {
                      apply(chip.getAttribute('data-dcs-chip') === active ? '' : chip.getAttribute('data-dcs-chip'), true);
                    });
                  });
                  sortChips(chipList);
                }
              })
              .catch(function () {
                // A page that will not load just means fewer designs, not a broken grid.
              });
          });
        })(page);
      }

      chain.then(function () {
        // Re-run both so appended cards honour the active chip and device.
        apply(active, false);
        if (device) applyDevice(device);
      });
    }

    function sortChips(chipList) {
      // "All" is already first and stays there; the rest are re-appended in order.
      var items = Array.prototype.slice.call(chipList.children);
      items.shift();
      items.sort(function (a, b) {
        return a.textContent.trim().localeCompare(b.textContent.trim());
      });
      items.forEach(function (item) {
        chipList.appendChild(item);
      });
    }

    var initial = '';
    var initialDevice = '';
    try {
      var search = new URL(window.location.href).searchParams;
      initial = search.get(QUERY_KEY) || '';
      initialDevice = search.get('device') || '';
    } catch (error) {
      initial = '';
    }
    // An unknown ?theme= falls back to "All" rather than to an empty grid.
    apply(initial, false);
    if (initialDevice) applyDevice(initialDevice);
    loadRemainingPages();

    on(document, 'dcs:device', function (event) {
      applyDevice(event.detail && event.detail.device);
    });

    return {
      apply: apply,
      destroy: function () {
        listeners.forEach(function (entry) {
          entry[0].removeEventListener(entry[1], entry[2]);
        });
        listeners.length = 0;
      },
    };
  }

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-dcs-design-themes]');

    Array.prototype.forEach.call(roots, function (root) {
      if (instances.has(root)) return;
      var instance = create(root);
      if (instance) instances.set(root, instance);
    });
  }

  function destroy(scope) {
    var roots = (scope || document).querySelectorAll('[data-dcs-design-themes]');

    Array.prototype.forEach.call(roots, function (root) {
      var instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroy(event.target);
  });

  /*
   * Theme editor: selecting a design card block must reveal it, even when the
   * active chip would otherwise filter it out.
   */
  document.addEventListener('shopify:block:select', function (event) {
    var card = event.target.closest ? event.target.closest('[data-dcs-card]') : null;
    if (!card) return;

    var root = card.closest('[data-dcs-design-themes]');
    var instance = root && instances.get(root);
    if (!instance) return;

    if (card.hasAttribute('hidden')) instance.apply('', false);
    card.scrollIntoView({ block: 'center', behavior: 'smooth' });
  });
})();
